import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import {
  FaEye,
  FaTrashAlt,
  FaSearch,
  FaTimes,
  FaUser,
  FaShippingFast,
  FaCreditCard,
  FaBoxOpen,
  FaCalendarAlt,
  FaTruck,
  FaBan,
  FaUndoAlt,
  FaRoute,
  FaCheckCircle,
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { fetchUnverifiedOrders, fetchVerifiedOrders } from '../api/api';

// const API_BASE_URL = 'http://localhost:8484';
// Production deploy ke time upar wali line comment karke ye use karna:
const API_BASE_URL = 'https://www.ayushvatika.com';

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const safeUpper = (value: any) => String(value || '').trim().toUpperCase();

const normalizePartner = (partner: any) => {
  const p = safeUpper(partner).replace(/\s+/g, '_');
  if (p.includes('EKART')) return 'EKART';
  if (p.includes('SHIPROCKET')) return 'SHIPROCKET';
  if (p.includes('DELHIVERY')) return 'DELHIVERY';
  return p || 'DELHIVERY';
};

const normalizeStatusText = (value: any) => {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/-/g, ' ')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ');
};

const standardizeStatus = (status: any) => {
  const s = normalizeStatusText(status);
  if (!s || s === 'UNKNOWN' || s === 'TRACKING INFO FETCHED') return null;

  // RTO / Return statuses sabse pehle handle karo
  if (
    (s.includes('RTO') || s.includes('RETURN TO ORIGIN') || s.includes('RETURNED') || s.includes('RETURN')) &&
    s.includes('DELIVER')
  ) {
    return 'RTO_DELIVERED';
  }

  if (
    (s.includes('RTO') || s.includes('RETURN TO ORIGIN') || s.includes('RETURNED') || s.includes('RETURN')) &&
    (s.includes('TRANSIT') || s.includes('IN TRANSIT'))
  ) {
    return 'RTO_IN_TRANSIT';
  }

  if (s.includes('RTO') && s.includes('NDR')) return 'RTO_NDR';
  if (s.includes('RTO')) return s.replace(/\s+/g, '_');
  if (s.includes('RETURN TO ORIGIN')) return 'RTO_IN_TRANSIT';

  // Normal statuses
  if (s === 'IN TRANSIT') return 'IN_TRANSIT';
  if (s === 'OUT FOR DELIVERY') return 'OUT_FOR_DELIVERY';
  if (s === 'ORDER PLACED') return 'PENDING';
  if (s === 'DISPATCHED') return 'SHIPPED';
  if (s === 'DELIVERED') return 'DELIVERED';
  if (s === 'CANCELLED' || s === 'CANCELED') return 'CANCELLED';

  return s.replace(/\s+/g, '_');
};

const isRtoStatus = (status: any) => {
  const s = normalizeStatusText(status);
  if (!s) return false;

  return (
    s.includes('RTO') ||
    s.includes('RETURN TO ORIGIN') ||
    s.includes('RETURNED') ||
    s.includes('RETURN IN TRANSIT') ||
    s.includes('RETURN DELIVERED') ||
    s.includes('RETURN_TO_ORIGIN') ||
    s.includes('RTO_IN_TRANSIT') ||
    s.includes('RTO_DELIVERED')
  );
};

const findStatusDeep = (obj: any, depth = 0): string | null => {
  if (!obj || depth > 5) return null;

  if (typeof obj === 'string') {
    const s = normalizeStatusText(obj);
    if (
      s.includes('RTO') ||
      s.includes('RETURN TO ORIGIN') ||
      s.includes('RETURNED') ||
      s.includes('IN TRANSIT') ||
      s.includes('OUT FOR DELIVERY') ||
      s.includes('DELIVERED') ||
      s.includes('CANCELLED') ||
      s.includes('CANCELED') ||
      s.includes('MANIFESTED') ||
      s.includes('DISPATCHED')
    ) {
      return obj;
    }
    return null;
  }

  if (Array.isArray(obj)) {
    for (const item of obj) {
      const found = findStatusDeep(item, depth + 1);
      if (found) return found;
    }
    return null;
  }

  if (typeof obj === 'object') {
    const priorityKeys = [
      'currentStatus',
      'current_status',
      'shipmentStatus',
      'shipment_status',
      'trackingStatus',
      'tracking_status',
      'courierStatus',
      'courier_status',
      'status',
      'Status',
      'instructions',
      'Instructions',
    ];

    for (const key of priorityKeys) {
      if (obj[key]) {
        const found = findStatusDeep(obj[key], depth + 1);
        if (found) return found;
      }
    }

    for (const key of Object.keys(obj)) {
      const found = findStatusDeep(obj[key], depth + 1);
      if (found) return found;
    }
  }

  return null;
};

const extractLiveStatus = (data: any, partner: string) => {
  if (!data) return null;

  // Backend agar clean currentStatus de raha hai to sabse pehle wahi use karo
  const directStatus =
    data.currentStatus ||
    data.status ||
    data.shipmentStatus ||
    data.trackingStatus ||
    data.courierStatus ||
    data.awbStatus;

  if (directStatus && safeUpper(directStatus) !== 'UNKNOWN') {
    const cleaned = standardizeStatus(directStatus);
    if (cleaned) return cleaned;
  }

  // rawResponse string ho to parse karo
  if (data.rawResponse) {
    try {
      const raw = typeof data.rawResponse === 'string' ? JSON.parse(data.rawResponse) : data.rawResponse;

      // Delhivery ka common response path
      const delhiveryStatus = raw?.ShipmentData?.[0]?.Shipment?.Status?.Status;
      if (delhiveryStatus) {
        const cleaned = standardizeStatus(delhiveryStatus);
        if (cleaned) return cleaned;
      }

      const delhiveryInstructions = raw?.ShipmentData?.[0]?.Shipment?.Status?.Instructions;
      if (delhiveryInstructions) {
        const cleaned = standardizeStatus(delhiveryInstructions);
        if (cleaned) return cleaned;
      }

      // Shiprocket / Ekart / generic response me nested status dhoondo
      const found = findStatusDeep(raw);
      if (found) {
        const cleaned = standardizeStatus(found);
        if (cleaned) return cleaned;
      }
    } catch (e) {
      console.error('Raw tracking response parse error:', e);
    }
  }

  const found = findStatusDeep(data);
  if (found) return standardizeStatus(found);

  return null;
};

const getWaybill = (o: any) => {
  return (
    o?.delhiveryWaybill ||
    o?.waybill ||
    o?.awb ||
    o?.awbCode ||
    o?.trackingId ||
    o?.shipmentId ||
    ''
  );
};

const getCustomerName = (order: any) => {
  const first = order?.user?.firstName || '';
  const last = order?.user?.lastName || '';
  return `${first} ${last}`.trim() || 'Customer';
};

const ReturnedOrderList: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [trackingRunning, setTrackingRunning] = useState<boolean>(false);
  const [isAgent, setIsAgent] = useState<boolean>(false);

  const [searchIdOrName, setSearchIdOrName] = useState<string>('');
  const [filterRtoStatus, setFilterRtoStatus] = useState<string>('All');
  const [filterPayment, setFilterPayment] = useState<string>('All');
  const [filterDate, setFilterDate] = useState<string>('');

  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    const checkUserRole = () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const payload = JSON.parse(
            decodeURIComponent(
              window
                .atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
            )
          );
          if (JSON.stringify(payload).toUpperCase().includes('AGENT')) setIsAgent(true);
        }
      } catch (e) {
        console.error('Error decoding token', e);
      }
    };

    checkUserRole();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      const [unverifiedRes, verifiedRes] = await Promise.all([
        fetchUnverifiedOrders().catch(() => ({ data: [] })),
        fetchVerifiedOrders().catch(() => ({ data: [] })),
      ]);

      const crmOrders = [...(unverifiedRes.data || []), ...(verifiedRes.data || [])].map((o: any) => {
        const waybill = getWaybill(o);
        const hasWaybill = waybill && String(waybill).trim() !== '';
        const initialStatus = standardizeStatus(o.status) || (hasWaybill ? 'MANIFESTED' : 'UNVERIFIED');

        return {
          id: o.id,
          user: {
            firstName: o.mainUser?.firstName || o.customerName || 'CRM Customer',
            lastName: o.mainUser?.lastName || '',
            email: o.mainUser?.email || o.email || 'N/A',
            mobile: o.mainUser?.mobile || o.mobile || 'N/A',
          },
          orderDate: o.createdAt || o.orderDate || new Date().toISOString(),
          delhiveryWaybill: hasWaybill ? String(waybill).trim() : null,
          deliveryPartner: normalizePartner(o.deliveryPartner || o.partner || o.courierPartner),
          totalAmount: o.payableAmount || o.totalAmount || 0,
          status: initialStatus,
          paymentMode: o.paymentMode || 'COD',
          paymentStatus: initialStatus === 'DELIVERED' || initialStatus === 'RTO_DELIVERED' ? 'PAID' : (o.paymentStatus || 'PENDING'),
          source: 'CRM',
          originalData: o,
        };
      });

      let unifiedOrders: any[] = [];

      if (isAgent) {
        unifiedOrders = crmOrders;
      } else {
        let webOrders: any[] = [];

        try {
          const webRes = await axios.get(`${API_BASE_URL}/api/orders/all`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          webOrders = (webRes.data || []).map((o: any) => {
            const waybill = getWaybill(o);
            const hasWaybill = waybill && String(waybill).trim() !== '';
            const initialStatus = standardizeStatus(o.status || o.orderStatus || o.shippingStatus) || (hasWaybill ? 'MANIFESTED' : 'PLACED');

            return {
              ...o,
              id: o.id,
              user: {
                firstName: o.user?.firstName || o.customer?.firstName || o.customerName || o.name || 'Website Customer',
                lastName: o.user?.lastName || o.customer?.lastName || '',
                email: o.user?.email || o.customer?.email || o.email || 'N/A',
                mobile: o.user?.mobile || o.customer?.mobile || o.mobile || o.phone || 'N/A',
              },
              orderDate: o.orderDate || o.createdAt || new Date().toISOString(),
              delhiveryWaybill: hasWaybill ? String(waybill).trim() : null,
              deliveryPartner: normalizePartner(o.deliveryPartner || o.partner || o.courierPartner),
              totalAmount: o.totalAmount || o.payableAmount || o.amount || 0,
              status: initialStatus,
              paymentMode: o.paymentMode || 'COD',
              paymentStatus: o.paymentStatus || 'PENDING',
              source: 'WEBSITE',
              originalData: o,
            };
          });
        } catch (error: any) {
          console.error('Website orders API failed:', error?.response?.data || error);
          toast.warning('Website orders load nahi hue, CRM orders show ho rahe hain.');
        }

        unifiedOrders = [...webOrders, ...crmOrders];
      }

      unifiedOrders.sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
      setOrders(unifiedOrders);

      // 🔥 RTO page ke liye har AWB ka live tracking check hoga
      // Kyunki RTO status DB me save nahi hai, courier live status se hi page dynamic banega.
      const trackableOrders = unifiedOrders.filter(o => o.delhiveryWaybill && String(o.delhiveryWaybill).trim() !== '');

      if (trackableOrders.length > 0) {
        setTrackingRunning(true);
        (async () => {
          for (const order of trackableOrders) {
            try {
              const partner = normalizePartner(order.deliveryPartner);
              const res = await axios.post(
                `${API_BASE_URL}/api/delivery/track?partner=${partner}`,
                { waybill: order.delhiveryWaybill },
                { headers: { Authorization: `Bearer ${token}` } }
              );

              const liveStatus = extractLiveStatus(res.data, partner);

              if (liveStatus) {
                setOrders(prev =>
                  prev.map(o =>
                    String(o.id) === String(order.id) && o.source === order.source
                      ? {
                          ...o,
                          status: liveStatus,
                          deliveryPartner: partner,
                          liveTrackingData: res.data,
                        }
                      : o
                  )
                );
              }
            } catch (e) {
              console.error(`RTO tracking failed for AWB: ${order.delhiveryWaybill}`, e);
            }

            // Rate limit se bachne ke liye thoda delay
            await sleep(250);
          }
          setTrackingRunning(false);
        })();
      }
    } catch (err: any) {
      console.error(err);
      toast.error('Returned/RTO orders load karne mein error aayi!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgent]);

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const currentStatus = order.status || '';

      // ✅ Sirf RTO / Return to origin orders hi show honge
      if (!isRtoStatus(currentStatus)) return false;

      const statusText = normalizeStatusText(currentStatus).replace(/\s+/g, '_');

      const matchesRtoStatus =
        filterRtoStatus === 'All' ||
        (filterRtoStatus === 'RTO_IN_TRANSIT' && statusText.includes('RTO') && statusText.includes('TRANSIT')) ||
        (filterRtoStatus === 'RTO_DELIVERED' && statusText.includes('RTO') && statusText.includes('DELIVER')) ||
        statusText === filterRtoStatus;

      const customerFullName = getCustomerName(order).toLowerCase();
      const search = searchIdOrName.toLowerCase();
      const matchesSearch =
        String(order.id).toLowerCase().includes(search) ||
        customerFullName.includes(search) ||
        (order.delhiveryWaybill && String(order.delhiveryWaybill).toLowerCase().includes(search)) ||
        (order.deliveryPartner && String(order.deliveryPartner).toLowerCase().includes(search));

      const matchesPayment =
        filterPayment === 'All' ||
        order.paymentMode === filterPayment ||
        order.paymentStatus === filterPayment;

      const orderDateOnly = new Date(order.orderDate).toISOString().split('T')[0];
      const matchesDate = !filterDate || orderDateOnly === filterDate;

      return matchesRtoStatus && matchesSearch && matchesPayment && matchesDate;
    });
  }, [orders, searchIdOrName, filterRtoStatus, filterPayment, filterDate]);

  const stats = useMemo(() => {
    const rtoOrders = orders.filter(o => isRtoStatus(o.status));
    const rtoInTransit = rtoOrders.filter(o => normalizeStatusText(o.status).includes('TRANSIT')).length;
    const rtoDelivered = rtoOrders.filter(o => normalizeStatusText(o.status).includes('DELIVER')).length;
    return {
      total: rtoOrders.length,
      inTransit: rtoInTransit,
      delivered: rtoDelivered,
    };
  }, [orders]);

  const handleDelete = async (id: string, source: string) => {
    if (!window.confirm('Kya aap is returned/RTO order ko delete karna chahte hain?')) return;

    try {
      const token = localStorage.getItem('token');

      if (source === 'CRM') {
        await axios.delete(`https://www.ayushvatika.com/api/admin/delete-order/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Local testing ke liye:
        // await axios.delete(`${API_BASE_URL}/api/admin/delete-order/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.delete(`https://www.ayushvatika.com/api/orders/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Local testing ke liye:
        // await axios.delete(`${API_BASE_URL}/api/orders/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      }

      toast.success('Returned/RTO order deleted!');
      fetchOrders();
    } catch (err: any) {
      console.error(err);
      toast.error('Delete failed!');
    }
  };

  const handleTrack = (waybill: string, partner: string) => {
    if (!waybill) return;

    const p = normalizePartner(partner);
    if (p === 'EKART') {
      window.open(`https://ekartlogistics.com/shipmenttrack/${waybill}`, '_blank');
    } else if (p === 'SHIPROCKET') {
      window.open('https://www.shiprocket.in/shipment-tracking/', '_blank');
    } else {
      window.open(`https://www.delhivery.com/track/package/${waybill}`, '_blank');
    }
  };

  const getStatusBadge = (status: string) => {
    const s = normalizeStatusText(status);

    if (s.includes('RTO') && s.includes('DELIVER')) {
      return (
        <span className="px-3 py-1 rounded-lg text-xs font-black bg-emerald-100 text-emerald-700 border border-emerald-200 flex items-center gap-2 w-fit">
          <FaCheckCircle /> RTO DELIVERED
        </span>
      );
    }

    if (s.includes('RTO') && s.includes('TRANSIT')) {
      return (
        <span className="px-3 py-1 rounded-lg text-xs font-black bg-orange-100 text-orange-700 border border-orange-200 flex items-center gap-2 w-fit">
          <FaRoute /> RTO IN TRANSIT
        </span>
      );
    }

    return (
      <span className="px-3 py-1 rounded-lg text-xs font-black bg-red-100 text-red-700 border border-red-200 flex items-center gap-2 w-fit">
        <FaUndoAlt /> {s.replace(/\s+/g, ' ') || 'RTO'}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="bg-gray-50 dark:bg-gray-900 p-6 min-h-screen transition-colors">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-xl shadow-sm">
              <FaUndoAlt size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">Returned / RTO Orders</h1>
              <p className="text-xs font-bold text-gray-400 mt-1">
                RTO in transit aur RTO delivered orders live courier tracking se show honge.
              </p>
            </div>
          </div>

          <button
            onClick={fetchOrders}
            className="px-5 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-sm transition-all"
          >
            Refresh Live Status
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm">
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Total RTO</p>
            <p className="text-3xl font-black text-gray-900 dark:text-white mt-2">{stats.total}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-orange-100 dark:border-gray-700 shadow-sm">
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">RTO In Transit</p>
            <p className="text-3xl font-black text-orange-600 mt-2">{stats.inTransit}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-emerald-100 dark:border-gray-700 shadow-sm">
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">RTO Delivered</p>
            <p className="text-3xl font-black text-emerald-600 mt-2">{stats.delivered}</p>
          </div>
        </div>

        {trackingRunning && (
          <div className="mb-6 rounded-2xl border border-orange-200 bg-orange-50 px-5 py-4 text-sm font-bold text-orange-700">
            Live tracking update chal raha hai... RTO orders status milte hi table me automatically aa jayenge.
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-10 bg-orange-50/50 dark:bg-orange-900/10 p-6 rounded-2xl border border-orange-100 dark:border-orange-900/30">
          <div>
            <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block ml-1">Search ID / Name / AWB</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Find RTO order..."
                className="w-full p-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                value={searchIdOrName}
                onChange={e => setSearchIdOrName(e.target.value)}
              />
              <FaSearch className="absolute left-3.5 top-4 text-gray-400" />
            </div>
          </div>

          <div>
            <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block ml-1">RTO Status</label>
            <select
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-500 shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              value={filterRtoStatus}
              onChange={e => setFilterRtoStatus(e.target.value)}
            >
              <option value="All">All RTO Status</option>
              <option value="RTO_IN_TRANSIT">RTO In Transit</option>
              <option value="RTO_DELIVERED">RTO Delivered</option>
              <option value="RTO_NDR">RTO NDR</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block ml-1">Payment</label>
            <select
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-500 shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              value={filterPayment}
              onChange={e => setFilterPayment(e.target.value)}
            >
              <option value="All">All Methods</option>
              <option value="COD">COD</option>
              <option value="PAID">PAID</option>
              <option value="PENDING">PENDING</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block ml-1">Date</label>
            <div className="relative">
              <input
                type="date"
                className="w-full p-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-500 shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                value={filterDate}
                onChange={e => setFilterDate(e.target.value)}
              />
              <FaCalendarAlt className="absolute left-3.5 top-4 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-2xl border border-gray-100 dark:border-gray-700 shadow-inner bg-white dark:bg-gray-800">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-100/50 dark:bg-gray-700/50">
              <tr className="text-[11px] font-black text-gray-500 uppercase tracking-tighter">
                <th className="px-6 py-5 text-left">Order ID</th>
                <th className="px-6 py-5 text-left">Customer</th>
                <th className="px-6 py-5 text-left">Date</th>
                <th className="px-6 py-5 text-left">Tracking (AWB)</th>
                <th className="px-6 py-5 text-left">Amount</th>
                <th className="px-6 py-5 text-left">RTO Status</th>
                <th className="px-6 py-5 text-center">Actions</th>
              </tr>
            </thead>

            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-20 text-gray-400 italic">
                    Loading RTO orders...
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-20 text-gray-400 font-bold">
                    No RTO orders found. Live tracking complete hone ke baad agar RTO hoga to yaha show hoga.
                  </td>
                </tr>
              ) : (
                filteredOrders.map(order => (
                  <tr key={`${order.source}-${order.id}`} className="hover:bg-orange-50/20 transition-all duration-200">
                    <td className="px-6 py-5 text-xs font-mono font-black text-orange-700 dark:text-orange-400 tracking-tight">
                      {String(order.id).substring(0, 8)} <br />
                      <span className="text-[10px] text-gray-400 uppercase">{order.source}</span>
                    </td>

                    <td className="px-6 py-5">
                      <div className="text-sm font-black text-gray-900 dark:text-white">{getCustomerName(order)}</div>
                      <div className="text-[10px] text-gray-400 font-medium">{order.user?.mobile || 'N/A'}</div>
                    </td>

                    <td className="px-6 py-5 text-xs text-gray-600 dark:text-gray-400 font-bold">
                      <div>{new Date(order.orderDate).toLocaleDateString('en-GB')}</div>
                    </td>

                    <td className="px-6 py-5">
                      {order.delhiveryWaybill ? (
                        <span className="text-xs font-mono font-bold text-purple-700 bg-purple-50 px-2 py-1 rounded inline-block">
                          {order.delhiveryWaybill} <br />
                          <span className="text-[9px] text-gray-500 uppercase">{order.deliveryPartner || 'DELHIVERY'}</span>
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-400 italic">N/A</span>
                      )}
                    </td>

                    <td className="px-6 py-5 text-base font-black text-gray-900 dark:text-white">₹{order.totalAmount}</td>
                    <td className="px-6 py-5">{getStatusBadge(order.status)}</td>

                    <td className="px-6 py-5 text-center">
                      <div className="flex justify-center gap-3">
                        {order.delhiveryWaybill && (
                          <button
                            onClick={() => handleTrack(order.delhiveryWaybill, order.deliveryPartner)}
                            className="p-2.5 text-purple-600 hover:bg-purple-100 rounded-xl transition-all shadow-sm"
                            title="Track Order"
                          >
                            <FaTruck size={18} />
                          </button>
                        )}

                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowModal(true);
                          }}
                          className="p-2.5 text-blue-600 hover:bg-blue-100 rounded-xl transition-all shadow-sm"
                          title="View Details"
                        >
                          <FaEye size={18} />
                        </button>

                        {!isAgent && (
                          <button
                            onClick={() => handleDelete(order.id, order.source)}
                            className="p-2.5 text-red-600 hover:bg-red-100 rounded-xl transition-all shadow-sm"
                            title="Delete Order"
                          >
                            <FaTrashAlt size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[100] p-4 animate-fade-in">
          <div className="bg-white rounded-[2rem] max-w-4xl w-full max-h-[92vh] overflow-y-auto border-4 border-gray-50">
            <div className="sticky top-0 bg-white/80 backdrop-blur-sm px-10 py-6 border-b border-gray-100 flex justify-between items-center z-10">
              <div>
                <h2 className="text-3xl font-black text-gray-900 tracking-tighter">
                  RTO Order Details <span className="text-sm bg-gray-200 px-2 rounded">{selectedOrder.source}</span>
                </h2>
                <p className="text-xs text-orange-600 font-mono mt-1 font-bold">ID: {selectedOrder.id}</p>
              </div>

              <button
                onClick={() => setShowModal(false)}
                className="p-3 hover:bg-gray-100 rounded-full text-gray-400 hover:text-red-500 transition-all"
              >
                <FaTimes size={28} />
              </button>
            </div>

            <div className="p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12">
                <div className="space-y-8">
                  <div>
                    <h3 className="text-xs font-black text-blue-600 uppercase tracking-[0.3em] flex items-center gap-3 mb-5">
                      <FaUser size={16} /> Customer
                    </h3>
                    <div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100 shadow-inner">
                      <p className="text-xl font-black text-gray-900">{getCustomerName(selectedOrder)}</p>
                      <p className="text-sm text-gray-500 mt-1">{selectedOrder.user?.email || 'N/A'}</p>
                      <p className="text-sm text-gray-400 mt-0.5">{selectedOrder.user?.mobile || 'N/A'}</p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xs font-black text-emerald-600 uppercase tracking-[0.3em] flex items-center gap-3 mb-5">
                      <FaShippingFast size={16} /> Shipping Point
                    </h3>
                    <div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100 text-sm text-gray-700 leading-relaxed font-semibold italic">
                      {selectedOrder.source === 'WEBSITE'
                        ? selectedOrder.originalData.shippingAddressSnapshot || selectedOrder.originalData.deliveryAddress || 'N/A'
                        : `${selectedOrder.originalData.houseNumber || ''}, ${selectedOrder.originalData.streetArea || ''}, ${selectedOrder.originalData.city || ''}, ${selectedOrder.originalData.state || ''} - ${selectedOrder.originalData.pincode || ''}`}
                    </div>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <h3 className="text-xs font-black text-orange-600 uppercase tracking-[0.3em] flex items-center gap-3 mb-5">
                      <FaCreditCard size={16} /> RTO Status & Payment
                    </h3>
                    <div className="bg-gray-50/50 p-7 rounded-3xl border border-gray-100 shadow-inner space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 font-bold uppercase">RTO Status</span>
                        {getStatusBadge(selectedOrder.status)}
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 font-bold uppercase">Payment</span>
                        <span className="text-sm font-black text-gray-900">{selectedOrder.paymentMode}</span>
                      </div>

                      {selectedOrder.delhiveryWaybill && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <p className="text-xs text-gray-500 uppercase font-bold mb-1">Tracking AWB</p>
                          <div className="flex justify-between items-center gap-4">
                            <div>
                              <span className="text-lg font-mono font-bold text-purple-700">{selectedOrder.delhiveryWaybill}</span>
                              <p className="text-[10px] font-black text-gray-400 uppercase mt-1">{selectedOrder.deliveryPartner}</p>
                            </div>
                            <button
                              onClick={() => handleTrack(selectedOrder.delhiveryWaybill, selectedOrder.deliveryPartner)}
                              className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded shadow-sm"
                            >
                              Track Now
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="flex justify-between items-end border-t border-gray-200 pt-4 mt-4">
                        <span className="text-lg font-black text-gray-600">Total</span>
                        <span className="text-4xl font-black text-orange-700">₹{selectedOrder.totalAmount}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-10 border-t-2 border-gray-50">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.4em] mb-8 flex items-center gap-3">
                  <FaBoxOpen size={18} /> Ordered Items
                </h3>

                <div className="grid grid-cols-1 gap-5">
                  {selectedOrder.source === 'WEBSITE' && Array.isArray(selectedOrder.originalData.orderItems) ? (
                    selectedOrder.originalData.orderItems.map((item: any, idx: number) => (
                      <div key={idx} className="flex items-center gap-6 bg-white p-5 border-2 border-gray-50 rounded-[1.5rem] hover:border-orange-300 transition-all">
                        <div className="w-20 h-20 bg-gray-50 rounded-2xl overflow-hidden flex-shrink-0">
                          <img src={item.imageUrl || '/Logo/default-product.png'} className="w-full h-full object-cover" alt="" />
                        </div>
                        <div className="flex-1">
                          <p className="text-lg font-black text-gray-900">{item.productName}</p>
                          <p className="text-sm text-gray-400 font-bold mt-1">
                            Qty: {item.quantity} | ₹{item.price}
                          </p>
                        </div>
                        <div className="text-2xl font-black text-gray-900 pr-4">₹{Number(item.quantity || 0) * Number(item.price || 0)}</div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center gap-6 bg-white p-5 border-2 border-gray-50 rounded-[1.5rem] hover:border-orange-300 transition-all">
                      <div className="w-20 h-20 bg-gray-50 rounded-2xl overflow-hidden flex-shrink-0 flex items-center justify-center">
                        <FaBoxOpen size={32} className="text-gray-300" />
                      </div>
                      <div className="flex-1">
                        <p className="text-lg font-black text-gray-900">{selectedOrder.originalData.medicine || selectedOrder.originalData.productName || 'Medicine'}</p>
                        <p className="text-sm text-gray-400 font-bold mt-1">
                          Qty: {selectedOrder.originalData.medicineQuantity || 1} | ₹{selectedOrder.originalData.medicinePrice || selectedOrder.totalAmount}
                        </p>
                      </div>
                      <div className="text-2xl font-black text-gray-900 pr-4">₹{selectedOrder.originalData.payableAmount || selectedOrder.totalAmount}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-10 py-8 bg-gray-50/80 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="px-12 py-4 bg-gray-900 text-white rounded-2xl text-xs font-black uppercase tracking-[0.2em] hover:bg-black transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ReturnedOrderList;
